/**
 * Breadcrumb Generator Utility
 * 
 * Generates breadcrumb navigation items from URL paths or data objects.
 * Supports automatic generation from URL structure and manual override.
 */

export interface BreadcrumbItem {
  name: string;
  url: string;
}

/**
 * Category name formatting map for common categories
 */
const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  dental: "Dental",
  cardiac: "Cardiac",
  orthopedics: "Orthopedics",
  oncology: "Oncology",
  fertility: "Fertility",
  transplants: "Transplants",
  ophthalmology: "Ophthalmology",
  cosmetic: "Cosmetic Surgery",
  wellness: "Wellness",
  rehabilitation: "Rehabilitation",
  mental_health: "Mental Health",
};

/**
 * Breadcrumb Generator class
 */
export class BreadcrumbGenerator {
  /**
   * Generate breadcrumbs from URL pathname
   * Automatically parses URL structure and creates hierarchy
   * 
   * @example
   * generateFromPath('/treatments/dental/dental-implants/')
   * // Returns: [
   * //   { name: 'Home', url: '/' },
   * //   { name: 'Treatments', url: '/treatments/' },
   * //   { name: 'Dental', url: '/treatments/dental/' },
   * //   { name: 'Dental Implants', url: '/treatments/dental/dental-implants/' }
   * // ]
   */
  static generateFromPath(pathname: string): BreadcrumbItem[] {
    // Remove trailing slash for processing, but keep it in URLs
    const cleanPath = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    const segments = cleanPath.split('/').filter(Boolean);

    // Homepage has no breadcrumbs
    if (segments.length === 0) {
      return [];
    }

    const breadcrumbs: BreadcrumbItem[] = [
      { name: 'Home', url: '/' }
    ];

    let currentPath = '';
    
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      currentPath += `/${segment}`;
      
      // Format the segment name
      const name = this.formatSegmentName(segment, i, segments);
      
      breadcrumbs.push({
        name,
        url: `${currentPath}/`
      });
    }

    return breadcrumbs;
  }

  /**
   * Generate breadcrumbs from data object
   * Useful when you have structured data about the current page
   * 
   * @example
   * generateFromData({
   *   type: 'treatment',
   *   category: 'dental',
   *   categoryName: 'Dental Procedures',
   *   name: 'Dental Implants',
   *   url: '/treatments/dental/dental-implants/'
   * })
   */
  static generateFromData(data: {
    type?: 'treatment' | 'blog' | 'hospital' | 'doctor';
    category?: string;
    categoryName?: string;
    name?: string;
    url: string;
  }): BreadcrumbItem[] {
    const breadcrumbs: BreadcrumbItem[] = [
      { name: 'Home', url: '/' }
    ];

    const { type, category, categoryName, name, url } = data;

    // Add type-level breadcrumb
    if (type) {
      const typeName = this.formatCategoryName(type);
      breadcrumbs.push({
        name: `${typeName}s`,
        url: `/${type}s/`
      });
    }

    // Add category breadcrumb
    if (category) {
      const catName = categoryName || this.formatCategoryName(category);
      const categoryUrl = type ? `/${type}s/${category}/` : `/${category}/`;
      breadcrumbs.push({
        name: catName,
        url: categoryUrl
      });
    }

    // Add current page
    if (name) {
      breadcrumbs.push({
        name,
        url
      });
    }

    return breadcrumbs;
  }

  /**
   * Format a URL segment into a display name
   */
  private static formatSegmentName(
    segment: string,
    index: number,
    allSegments: string[]
  ): string {
    // Check if it's a known category
    if (CATEGORY_DISPLAY_NAMES[segment]) {
      return CATEGORY_DISPLAY_NAMES[segment];
    }

    // Special handling for common segments
    if (segment === 'treatments') return 'Treatments';
    if (segment === 'blog') return 'Blog';
    if (segment === 'hospitals') return 'Hospitals';
    if (segment === 'doctors') return 'Doctors';
    if (segment === 'category') return 'Category';
    if (segment === 'compare-costs') return 'Compare Costs';
    if (segment === 'medical-tourism-india') return 'Medical Tourism India';
    if (segment === 'contact') return 'Contact';
    if (segment === 'privacy-policy') return 'Privacy Policy';
    if (segment === 'terms-of-service') return 'Terms of Service';

    // Default: convert kebab-case to Title Case
    return this.formatCategoryName(segment);
  }

  /**
   * Format category name from slug to display format
   * Converts kebab-case and snake_case to Title Case
   * 
   * @example
   * formatCategoryName('dental-implants') // 'Dental Implants'
   * formatCategoryName('mental_health') // 'Mental Health'
   */
  static formatCategoryName(category: string): string {
    if (!category) return '';

    return category
      .replace(/[-_]/g, ' ') // Replace hyphens and underscores with spaces
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Validate breadcrumb structure
   * Ensures all breadcrumbs have required properties
   */
  static validate(breadcrumbs: BreadcrumbItem[]): boolean {
    if (!Array.isArray(breadcrumbs)) return false;
    if (breadcrumbs.length === 0) return true; // Empty is valid (homepage)

    return breadcrumbs.every(item => 
      item &&
      typeof item.name === 'string' &&
      item.name.length > 0 &&
      typeof item.url === 'string' &&
      item.url.length > 0
    );
  }

  /**
   * Get the current page breadcrumb (last item)
   */
  static getCurrentPage(breadcrumbs: BreadcrumbItem[]): BreadcrumbItem | null {
    if (breadcrumbs.length === 0) return null;
    return breadcrumbs[breadcrumbs.length - 1];
  }

  /**
   * Get parent breadcrumbs (all except last)
   */
  static getParents(breadcrumbs: BreadcrumbItem[]): BreadcrumbItem[] {
    if (breadcrumbs.length <= 1) return [];
    return breadcrumbs.slice(0, -1);
  }
}
